import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import Button from '~/components/buttons/NewButton';
import TextController from '~/components/controllers/TextController/TextController';
import { VStack } from '~/components/layout/Stack';
import ModalActions from '~/components/modals/Modal/ModalActions';
import Text from '~/components/typographies/Text';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import IconSearch from '~/icons/icon-search.svg?react';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useSearchCompanyIdentity } from '~/services/company-identity';
import { CompanyIdentity } from '~/types/company-identity';
import { isItalianTaxNumber, isItalianVatNumber } from '~/utils/validation';
import styles from './AddCompanySearch.module.scss';

interface AddCompanySearchProps extends ModalProps {
  initialValue: string;
  onNotFound: (taxNumber: string) => void;
  onFound: (taxNumber: string, identity: CompanyIdentity, available: boolean) => void;
}

const useCompanySearchSchema = () => {
  const { t } = useTranslation('validations');
  return useMemo(
    () =>
      z.object({
        taxNumber: z
          .string()
          .min(1)
          .superRefine((v, ctx) => {
            if (isItalianVatNumber(v) || isItalianTaxNumber(v)) {
              return;
            }

            if (new RegExp(/\d+/).test(v)) {
              return ctx.addIssue({
                message: t('exact_string', { count: 11 }),
                code: z.ZodIssueCode.custom,
              });
            }

            return ctx.addIssue({
              message: t('exact_string', { count: 16 }),
              code: z.ZodIssueCode.custom,
            });
          })
          .transform((x) => x.toUpperCase()),
      }),
    [t],
  );
};

type FormInput = z.input<ReturnType<typeof useCompanySearchSchema>>;
type FormOutput = z.output<ReturnType<typeof useCompanySearchSchema>>;

const AddCompanySearch: React.FC<AddCompanySearchProps> = ({ closeModal, onFound, onNotFound, initialValue }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'add_company.steps.search_company' });
  const { captureException } = useExceptionLogger();
  const companySearchSchema = useCompanySearchSchema();
  const { track } = useAnalytics();

  const form = useForm<FormInput, null, FormOutput>({
    defaultValues: {
      taxNumber: initialValue,
    },
    resolver: zodResolver(companySearchSchema),
  });

  const searchCompanyIdentity = useSearchCompanyIdentity();

  const identifyCompany = async (taxNumber: string) => {
    try {
      const { data, meta } = await searchCompanyIdentity.mutateAsync({ taxNumber, country: 'IT' });
      onFound(taxNumber, data, meta.available);
    } catch (error) {
      captureException(error, {
        extra: {
          tax_number: taxNumber,
        },
      });
      onNotFound(taxNumber);
    }
  };
  return (
    <form
      noValidate
      onSubmit={form.handleSubmit((values) => {
        track(AnalyticsEvents.SECOND_COMPANIES_CREATION_STARTED, {
          step: 'company_searched',
          metadata: { tax_code: values.taxNumber },
        });
        return identifyCompany(values.taxNumber);
      })}
    >
      <VStack gap={5}>
        <VStack gap={2}>
          <Text variant="body2" weight="regular">
            <Trans t={t} i18nKey="description" />
          </Text>
          <TextController
            autoFocus
            className={styles.searchText}
            control={form.control}
            name="taxNumber"
            label={t('placeholder')}
            hideLabel
            maxLength={16}
            required
            placeholder={t('placeholder')}
          />
        </VStack>

        <ModalActions className={styles.actionsContainer}>
          <Button
            variant={'outlined'}
            onClick={() => {
              closeModal();
            }}
          >
            {t('actions.cancel')}
          </Button>
          <Button type="submit" loading={searchCompanyIdentity.isPending} Icon={IconSearch}>
            {t('actions.search')}
          </Button>
        </ModalActions>
      </VStack>
    </form>
  );
};

export default AddCompanySearch;
